import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ChannelType, EventStatus, Permissions as P, type GuildEvent } from '@comms/shared';
import { displayName, useStore } from '../../store';
import { api } from '../../lib/api';
import { has, usePermissions } from '../../hooks/permissions';
import { Button, ErrorText, Icon, Input, Modal, cx } from '../ui';

const NO_EVENTS: GuildEvent[] = [];

function when(e: GuildEvent) {
  const start = new Date(e.startAt);
  const date = start.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return e.endAt
    ? `${date} – ${new Date(e.endAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : date;
}

/** `<input type="datetime-local">` value for a date, in local time. */
function localInput(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventsModal({ guildId, onClose }: { guildId: string; onClose: () => void }) {
  const events = useStore((s) => s.guilds[guildId]?.events ?? NO_EVENTS);
  const perms = usePermissions(guildId);
  const [creating, setCreating] = useState(events.length === 0 && has(perms, P.CREATE_EVENTS));
  return (
    <Modal title="Events" onClose={onClose} wide>
      <div className="space-y-3">
        {has(perms, P.CREATE_EVENTS) && !creating && (
          <Button onClick={() => setCreating(true)}>Create event</Button>
        )}
        {creating && <CreateEvent guildId={guildId} onDone={() => setCreating(false)} />}
        {events.length === 0 && !creating && (
          <p className="text-sm text-text-3">No upcoming events.</p>
        )}
        {events.map((e) => (
          <EventCard key={e.id} event={e} guildId={guildId} />
        ))}
      </div>
    </Modal>
  );
}

function EventCard({ event: e, guildId }: { event: GuildEvent; guildId: string }) {
  const interested = useStore((s) => !!s.guilds[guildId]?.interestedEventIds.includes(e.id));
  const meId = useStore((s) => s.me?.id);
  const channelName = useStore((s) => (e.channelId ? s.channels[e.channelId]?.name : null));
  const creator = useStore((s) => (e.creatorId ? displayName(s, e.creatorId, guildId) : null));
  const perms = usePermissions(guildId);
  const [error, setError] = useState<unknown>(null);
  const canManage =
    has(perms, P.MANAGE_EVENTS) || (e.creatorId === meId && has(perms, P.CREATE_EVENTS));
  const live = e.status === EventStatus.ACTIVE;
  const act = (p: Promise<unknown>) => void p.catch(setError);
  const patch = (body: Record<string, unknown>) =>
    act(api('PATCH', `/guilds/${guildId}/events/${e.id}`, body));
  return (
    <div className="rounded-lg bg-bg-1 p-4" aria-label={e.name}>
      <div className={cx('text-xs font-semibold', live ? 'text-success' : 'text-accent')}>
        {live ? '● Happening now' : when(e)}
      </div>
      <h3 className="mt-1 text-lg font-semibold text-text-0">{e.name}</h3>
      {e.description && <p className="whitespace-pre-wrap text-sm text-text-2">{e.description}</p>}
      <div className="mt-2 flex items-center gap-1 text-sm text-text-3">
        <Icon name={e.channelId ? 'speaker' : 'pin'} className="h-4 w-4" />
        {e.channelId ? (channelName ?? 'voice channel') : e.location}
        {creator && <span className="ml-3">by {creator}</span>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant={interested ? 'secondary' : 'primary'}
          onClick={() =>
            act(
              api(
                interested ? 'DELETE' : 'PUT',
                `/guilds/${guildId}/events/${e.id}/interested/@me`,
              ),
            )
          }
        >
          {interested ? '✓ Interested' : 'Interested'}
        </Button>
        <span className="text-sm text-text-3">{e.interestedCount} interested</span>
        {canManage && (
          <span className="ml-auto flex gap-2">
            {e.status === EventStatus.SCHEDULED && (
              <Button variant="secondary" onClick={() => patch({ status: EventStatus.ACTIVE })}>
                Start now
              </Button>
            )}
            {live && (
              <Button variant="secondary" onClick={() => patch({ status: EventStatus.COMPLETED })}>
                End event
              </Button>
            )}
            {e.status === EventStatus.SCHEDULED && (
              <Button
                variant="danger"
                onClick={() =>
                  confirm(`Cancel "${e.name}"?`) && patch({ status: EventStatus.CANCELED })
                }
              >
                Cancel event
              </Button>
            )}
          </span>
        )}
      </div>
      <ErrorText error={error} />
    </div>
  );
}

function CreateEvent({ guildId, onDone }: { guildId: string; onDone: () => void }) {
  const voiceChannels = useStore(
    useShallow((s) =>
      Object.values(s.channels)
        .filter((c) => c.guildId === guildId && c.type === ChannelType.GUILD_VOICE)
        .sort((a, b) => a.position - b.position),
    ),
  );
  const [where, setWhere] = useState<'voice' | 'elsewhere'>(
    voiceChannels.length ? 'voice' : 'elsewhere',
  );
  const [channelId, setChannelId] = useState(voiceChannels[0]?.id ?? '');
  const [location, setLocation] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(localInput(new Date(Date.now() + 3600_000)));
  const [end, setEnd] = useState('');
  const [error, setError] = useState<unknown>(null);

  async function create() {
    try {
      await api('POST', `/guilds/${guildId}/events`, {
        name,
        description,
        startAt: new Date(start).toISOString(),
        endAt: end ? new Date(end).toISOString() : null,
        ...(where === 'voice' ? { channelId } : { location }),
      });
      onDone();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="space-y-3 rounded-lg bg-bg-1 p-4">
      <div className="flex gap-2">
        {(['voice', 'elsewhere'] as const).map((w) => (
          <label
            key={w}
            className={cx(
              'flex flex-1 cursor-pointer items-center gap-2 rounded p-2 text-sm',
              where === w ? 'bg-bg-active text-text-0' : 'bg-bg-2 text-text-2',
            )}
          >
            <input
              type="radio"
              checked={where === w}
              disabled={w === 'voice' && voiceChannels.length === 0}
              onChange={() => setWhere(w)}
            />
            {w === 'voice' ? 'Voice channel' : 'Somewhere else'}
          </label>
        ))}
      </div>
      {where === 'voice' ? (
        <label className="block text-sm text-text-2">
          Channel
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="mt-1 block w-full rounded bg-bg-0 px-3 py-2 text-text-0"
          >
            {voiceChannels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <Input
          label="Location"
          value={location}
          maxLength={100}
          placeholder="Park, someone's place, a link…"
          onChange={(e) => setLocation(e.target.value)}
        />
      )}
      <Input
        label="Event name"
        value={name}
        maxLength={100}
        onChange={(e) => setName(e.target.value)}
      />
      <label className="block">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-text-2">
          Description
        </span>
        <textarea
          aria-label="Description"
          value={description}
          maxLength={1000}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded bg-bg-0 px-3 py-2 text-text-0 outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <div className="flex gap-3">
        <Input
          label="Starts"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
        <Input
          label="Ends (optional)"
          type="datetime-local"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>
      <ErrorText error={error} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button
          disabled={!name.trim() || !start || (where === 'voice' ? !channelId : !location.trim())}
          onClick={create}
        >
          Schedule event
        </Button>
      </div>
    </div>
  );
}
